import { ArrowDown, Loader2, RefreshCw, Send } from "lucide-react"
import React, { useEffect, useRef, useState } from "react"
import { toast } from "sonner"

import { getAIChatMessages } from "@/api/others/ai"
import { GoBack } from "@/components/shared/layout"
import { Button, Image, Input } from "@/components/ui"
import { EFolderAssets } from "@/modules/helpers/enum"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { useAIChat } from "@/modules/helpers/hooks/use-ai-chat"
import { getImageByNameFromS3 } from "@/modules/helpers/utils"
import type { AIMessage } from "@/modules/types/others"

export const AIChatPage: React.FC<{
  code?: string
  shopName?: string
  otherUserId?: string
  setOpen: (open: boolean) => void
}> = ({ setOpen }) => {
  const [initialMessages, setInitialMessages] = useState<AIMessage[]>([])
  const [isLoadingHistory, setIsLoadingHistory] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMoreHistory, setHasMoreHistory] = useState(true)
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  // Load chat history from API
  useEffect(() => {
    const loadChatHistory = async () => {
      try {
        setIsLoadingHistory(true)
        const response = await getAIChatMessages(1, 20)

        if (response.data?.data?.list) {
          const apiMessages = response.data.data.list

          // Transform API messages to AIMessage format
          // API returns messages in DESC order (newest first)
          // We need to reverse to get chronological order (oldest first)
          const transformedMessages: AIMessage[] = apiMessages
            .map((msg: any) => ({
              id: msg.id?.toString() || Date.now().toString(),
              content: msg.content || "",
              isAI: msg.role === "AI",
              timestamp: new Date(msg.createdAt).getTime(),
              createdAt: msg.createdAt,
              isTyping: false,
              isError: false
            }))
            .sort((a, b) => a.timestamp - b.timestamp) // Sort chronologically (oldest to newest)

          setInitialMessages(transformedMessages)

          // Check if there are more messages
          const total = response.data?.data?.total || 0
          setHasMoreHistory(apiMessages.length >= 20 && transformedMessages.length < total)
        }
      } catch (error) {
        console.error("Failed to load chat history:", error)
        setInitialMessages([])
      } finally {
        setIsLoadingHistory(false)
      }
    }

    loadChatHistory()
  }, [])

  // Load more messages when scrolling to top
  const loadMoreMessages = async () => {
    if (isLoadingMore || !hasMoreHistory) return

    // Save current scroll position BEFORE loading
    const container = messagesContainerRef.current
    if (!container) return

    const previousScrollHeight = container.scrollHeight
    const previousScrollTop = container.scrollTop

    try {
      setIsLoadingMore(true)
      const nextPage = currentPage + 1
      const response = await getAIChatMessages(nextPage, 20)

      if (response.data?.data?.list) {
        const apiMessages = response.data.data.list

        if (apiMessages.length === 0) {
          setHasMoreHistory(false)
          return
        }

        // Transform new messages from API (DESC order) to chronological order
        const transformedMessages: AIMessage[] = apiMessages
          .map((msg: any) => ({
            id: msg.id?.toString() || Date.now().toString(),
            content: msg.content || "",
            isAI: msg.role === "AI",
            timestamp: new Date(msg.createdAt).getTime(),
            createdAt: msg.createdAt,
            isTyping: false,
            isError: false
          }))
          .sort((a, b) => a.timestamp - b.timestamp) // Sort chronologically

        // Prepend to existing messages (these are older messages)
        setInitialMessages((prev) => [...transformedMessages, ...prev])
        setCurrentPage(nextPage)

        // Restore scroll position after DOM updates
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight
            const addedHeight = newScrollHeight - previousScrollHeight
            container.scrollTop = previousScrollTop + addedHeight
          }
        })

        // Check if there are more
        const total = response.data?.data?.total || 0
        const loadedCount = nextPage * 20
        setHasMoreHistory(loadedCount < total)
      }
    } catch {
      toast.error("Tải thêm tin nhắn thất bại")
    } finally {
      setIsLoadingMore(false)
    }
  }

  // Handle scroll event to detect top and show/hide scroll button
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget

    // Load more when scroll to top
    if (target.scrollTop === 0 && hasMoreHistory && !isLoadingMore && !isLoadingHistory) {
      loadMoreMessages()
    }

    // Show scroll button when not at bottom
    const isAtBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 100
    setShowScrollButton(!isAtBottom)
  }

  const { messages, isLoading, dailyRequestCount, dailyLimit, sendMessage, retryMessage } =
    useAIChat(initialMessages)
  const [inputMessage, setInputMessage] = useState("")
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const { user } = useCurrentUser()
  const hasScrolledToBottomRef = useRef(false)
  const [showScrollButton, setShowScrollButton] = useState(false)

  const scrollToLatestMessage = (smooth = false) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "instant" })
  }

  // Only scroll to bottom once after initial messages load
  useEffect(() => {
    // Only scroll once when initial messages are loaded for the first time
    if (!isLoadingHistory && initialMessages.length > 0 && !hasScrolledToBottomRef.current) {
      // Use setTimeout to ensure DOM has rendered
      setTimeout(() => {
        scrollToLatestMessage()
        hasScrolledToBottomRef.current = true
      }, 100)
    }
  }, [isLoadingHistory, initialMessages.length])

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return

    const message = inputMessage
    setInputMessage("")

    // Send message using the hook
    await sendMessage(message)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit"
    })
  }

  const getAvatarSrc = () => {
    if (!user?.avatarName) return null
    if (user.avatarName.includes("https")) {
      return user.avatarName
    }
    return getImageByNameFromS3(user.avatarName, user.id, EFolderAssets.AVATAR)
  }

  const remainingQuestions = dailyLimit - dailyRequestCount

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-white via-white to-gray-50 text-gray-900">
      <GoBack
        className="bg-white"
        title="ZZP - AI chat dành cho KOC"
        onClick={() => setOpen(false)}
      />

      {/* Error Display */}

      {/* Messages Container */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4 pt-6 space-y-4"
        onScroll={handleScroll}
      >
        {/* Load More Indicator */}
        {isLoadingMore && (
          <div className="flex justify-center items-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            <span className="ml-2 text-xs text-gray-500">Đang tải thêm...</span>
          </div>
        )}

        {!isLoadingMore && hasMoreHistory && messages.length > 0 && (
          <div className="flex justify-center py-2">
            <button
              onClick={loadMoreMessages}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Tải thêm tin nhắn cũ
            </button>
          </div>
        )}

        {/* Loading History Indicator */}
        {isLoadingHistory && (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Đang tải lịch sử chat...</span>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              "flex items-start gap-3 max-w-[85%]",
              message.isAI ? "self-start" : "self-end ml-auto flex-row-reverse"
            )}
          >
            {/* Avatar */}
            {message.isAI ? (
              <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border overflow-hidden">
                <Image
                  src="/favicon-96x96.png"
                  alt="AI avatar"
                  width={32}
                  height={32}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : getAvatarSrc() ? (
              <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden ring-2 ring-gray-200">
                <Image
                  src={getAvatarSrc()!}
                  alt="User avatar"
                  width={32}
                  height={32}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.src =
                      "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQsBGOs2225fFqTfnl5EKlrEUBn5-drby1x3Q&s"
                  }}
                />
              </div>
            ) : (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-xs text-gray-500">U</span>
              </div>
            )}

            {/* Message Bubble */}
            <div
              className={cn("rounded-2xl p-2 max-w-full", {
                "bg-red-50 text-red-800 border border-red-200": message.isAI && message.isError,
                "text-gray-800 border border-gray-200": message.isAI && !message.isError,
                "bg-gradient-to-br from-cyan-500 to-emerald-500 text-white shadow-md": !message.isAI
              })}
            >
              {message.isTyping ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-black">Đang soạn tin</span>
                  <div className="flex space-x-1">
                    <div className="w-1 h-1 bg-black/70 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div className="w-1 h-1 bg-black/70 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div className="w-1 h-1 bg-black/70 rounded-full animate-bounce"></div>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className="text-xs leading-relaxed whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: message?.content || "" }}
                  ></div>
                  <div className="flex items-center justify-between mt-1">
                    <p
                      className={cn(
                        "text-[10px] opacity-70",
                        message.isAI ? "text-green-50" : "text-white"
                      )}
                    >
                      {formatTime(message.timestamp || Date.now())}
                    </p>
                    {message.isError && message.question && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => retryMessage(message.question!)}
                        className="text-xs h-6 px-2 text-red-600 hover:text-red-700"
                        disabled={isLoading}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Thử lại
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}

        <div ref={messagesEndRef} />

        {/* Scroll to Bottom Button */}
        {showScrollButton && (
          <button
            onClick={() => scrollToLatestMessage(true)}
            className="fixed bottom-24 right-6 z-50 w-8 h-8 bg-primary-50 border-primary border text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center"
            aria-label="Scroll to bottom"
          >
            <ArrowDown size={14} color="#0091aa" />
          </button>
        )}
      </div>

      {/* Daily Request Count */}
      {dailyRequestCount < dailyLimit && (
        <div className="px-4 bg-white flex justify-start pt-2">
          <p className="text-xs text-gray-600 text-center">
            Còn lại {remainingQuestions}/{dailyLimit} câu hỏi hôm nay
          </p>
        </div>
      )}

      {/* Input Area */}
      <div className="border-gray-200 bg-white p-4 pt-2">
        <div className="flex items-end gap-2">
          {/* <div className="flex items-center gap-2">
            <button
              type="button"
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              disabled={isLoading || dailyRequestCount >= dailyLimit}
            >
              <Smile size={20} />
            </button>
            <button
              type="button"
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              disabled={isLoading || dailyRequestCount >= dailyLimit}
            >
              <Paperclip size={20} />
            </button>
            <button
              type="button"
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              disabled={isLoading || dailyRequestCount >= dailyLimit}
            >
              <FileText size={20} />
            </button>
          </div> */}
          <div className="flex-1">
            <Input
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                dailyRequestCount >= dailyLimit
                  ? "Bạn đã hết lượt hỏi hôm nay..."
                  : "Nhập vào tin nhắn"
              }
              className={cn(
                "resize-none bg-white text-gray-900 placeholder-gray-400 border-gray-300 focus:border-emerald-500 focus:ring-emerald-500",
                dailyRequestCount >= dailyLimit && "opacity-50"
              )}
              disabled={isLoading || dailyRequestCount >= dailyLimit}
            />
          </div>
          <Button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading || dailyRequestCount >= dailyLimit}
            className={cn(
              "h-10 w-10 rounded-full text-white shadow-lg",
              dailyRequestCount >= dailyLimit
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-gradient-primary"
            )}
          >
            {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AIChatPage
